import { Box, Button, Typography, Table } from "flux";
import { retryPublish } from "../../features/ad-publishing/api";
import useBatchPolling from "../../hooks/use-batch-polling";
import useBatches, { transformBatches } from "@adora/shared/hooks/use-batches";
import useBatchStore from "../../hooks/use-batch-store";
import { useEffect, useMemo } from "react";
import { BatchType } from "@adora/shared/types/batch";
import { getLatestBatchesForAdAssetGroup } from "@adora/shared/utils/batch";
import { Platform } from "@adora/shared/types/ad-platforms";

const cols = [
  {
    field: "id",
    value: "id",
    width: 200
  },
  {
    field: "platform",
    value: "platform",
    width: 200
  },
  {
    field: "status",
    value: "status",
    width: 200
  }
];

export default function BatchesTable({
  brandId,
  adAssetGroupId,
  platforms
}: {
  brandId: number;
  adAssetGroupId: number;
  platforms: Platform[];
}) {
  useBatchPolling();

  const { upsertBatches, batches } = useBatchStore();

  const { batches: initialBatches } = useBatches({
    type: BatchType.AdAssetGroupPublish,
    adAssetGroupIds: [adAssetGroupId]
  });

  useEffect(() => {
    if (initialBatches?.length) upsertBatches(initialBatches);
  }, [initialBatches, upsertBatches]);

  const latestBatches = getLatestBatchesForAdAssetGroup(adAssetGroupId, batches);

  const rows = useMemo(
    () =>
      latestBatches
        .map((batch) => ({ id: batch.id, platform: batch?.info?.platform, status: batch.status }))
        .filter((batch) => batch.platform && platforms.includes(batch.platform as Platform)),
    [latestBatches, platforms]
  );

  const handleRetry = async () => {
    const batches = await retryPublish(Number(brandId), Number(adAssetGroupId));
    batches.forEach((bs) => {
      const transformed = transformBatches(bs);
      if (transformed?.length) upsertBatches(transformed);
    });
  };

  return rows?.length ? (
    <Box>
      <Typography variant="h2" mb={2}>
        Publication Batches
      </Typography>
      {brandId && adAssetGroupId && (
        <Box mb={4}>
          <Button variant="contained" disabled={false} onClick={handleRetry} dataDogIdentifier="batches table retry">
            Retry
          </Button>
        </Box>
      )}
      <Table columns={cols} rows={rows} dataDogIdentifier="batches" />
    </Box>
  ) : null;
}
